import React, { useMemo } from "react";
import { Form, Table } from "react-bootstrap";
import { AuditAnswer } from "../../models/AuditAnswer";
import { AuditQuestion } from "../../models/AuditQuestion";

type Props = {
  answer: AuditAnswer;
  questions: AuditQuestion[];
  setQuestions: (value: AuditQuestion[]) => void;
};

export const AuditMatrixQuestion = (props: Props) => {
  const matrixStructure = useMemo(() => {
    const desc = props.answer.answerDesc;
    if (!desc || !desc.includes("}")) return { rows: [], columns: [] };

    const parts = desc.split("}");
    if (parts.length < 2) return { rows: [], columns: [] };

    try {
      const rows = JSON.parse(parts[0]);
      const columns = JSON.parse(parts[1]);
      return { rows, columns };
    } catch (e) {
      console.error(
        "Error al parsear estructura de matriz:",
        e,
        "Input:",
        desc
      );
      return { rows: [], columns: [] };
    }
  }, [props.answer.answerDesc]);

  const matrixAnswers = useMemo(() => {
    try {
      if (props.answer.answerText) {
        const outerArray = JSON.parse(props.answer.answerText);
        return outerArray.map((row: string) => JSON.parse(row));
      }
      return Array(matrixStructure.rows.length)
        .fill(null)
        .map(() => Array(matrixStructure.columns.length).fill(""));
    } catch (e) {
      console.error(
        "Error al parsear respuestas de matriz:",
        e,
        "Input:",
        props.answer.answerText
      );
      return [];
    }
  }, [props.answer.answerText, matrixStructure]);

  const onEditMatrixValue = (
    rowIndex: number,
    columnIndex: number,
    newValue: string
  ) => {
    const newAnswers = matrixAnswers.map((row: any[]) => [...row]);
    if (newAnswers[rowIndex]) {
      newAnswers[rowIndex][columnIndex] = newValue;
    }

    const newAnswerTextForStorage = JSON.stringify(
      newAnswers.map((row: any[]) => JSON.stringify(row))
    );

    const newQuestions = props.questions.map((q) => {
      if (q.id !== props.answer.auditQuestionId) return q;
      return {
        ...q,
        answerText: newAnswerTextForStorage,
        auditAnswers: q.auditAnswers?.map((ans) =>
          ans.id === props.answer.id
            ? { ...ans, answerText: newAnswerTextForStorage }
            : ans
        ),
      };
    });
    props.setQuestions(newQuestions);
  };

  return (
    <Table striped bordered hover responsive>
      <thead className="table-secondary">
        <tr>
          <th style={{ minWidth: "150px" }}></th>
          {matrixStructure.columns.map(
            (columnName: string, colIndex: number) => (
              <th key={`header-${colIndex}`} className="text-center">
                {columnName}
              </th>
            )
          )}
        </tr>
      </thead>
      <tbody>
        {matrixStructure.rows.map((rowName: string, rowIndex: number) => (
          <tr key={`row-${rowIndex}`} className="align-middle">
            <td className="fw-bold table-light">{rowName}</td>
            {matrixStructure.columns.map((_: string, colIndex: number) => (
              <td key={`cell-${rowIndex}-${colIndex}`}>
                <Form.Control
                  type="text"
                  value={matrixAnswers[rowIndex]?.[colIndex] || ""}
                  onChange={(e) =>
                    onEditMatrixValue(rowIndex, colIndex, e.target.value)
                  }
                />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
};
